import type { FieldDefinition, IntegrationProviderConfig } from '../../types';
import NotionConnectionCard from './NotionConnectionCard';
import NotionIcon from './NotionIcon';
import NotionPullPanelExtras from './NotionPullPanelExtras';
import NotionPushPanelExtras from './NotionPushPanelExtras';

const pullObjectTypes = [
  { label: 'Pages', value: 'pages' },
  { label: 'Database records', value: 'database_records' },
  { label: 'Blocks (page content)', value: 'blocks' },
];

const sortDirectionOptions = [
  { label: 'Ascending', value: 'ascending' },
  { label: 'Descending', value: 'descending' },
];

function getPullFields(objectType: string): FieldDefinition[] {
  if (objectType === 'pages') {
    return [
      {
        name: 'searchQuery',
        label: 'Search query',
        type: 'text',
        placeholder: 'Free-text search across shared pages',
        hasConnectionPoint: true,
      },
      {
        name: 'limit',
        label: 'Limit',
        type: 'number',
        defaultValue: 20,
        placeholder: 'Default 20, Max 100',
        validation: { maxLength: 3 },
      },
    ];
  }

  if (objectType === 'database_records') {
    return [
      {
        name: 'database',
        label: 'Database',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        placeholder: 'Search databases',
        validation: { required: true },
      },
      {
        name: 'sortProperty',
        label: 'Sort property',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        placeholder: 'Pick a property',
        hasConnectionPoint: true,
      },
      {
        name: 'sortDirection',
        label: 'Sort direction',
        type: 'select',
        options: sortDirectionOptions,
        defaultValue: 'descending',
      },
      {
        name: 'limit',
        label: 'Limit',
        type: 'number',
        defaultValue: 20,
        placeholder: 'Default 20, Max 100',
        validation: { maxLength: 3 },
      },
    ];
  }

  if (objectType === 'blocks') {
    return [
      {
        name: 'page_id',
        label: 'Page',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        placeholder: 'Search pages',
        validation: { required: true },
        hasConnectionPoint: true,
      },
      {
        name: 'limit',
        label: 'Limit',
        type: 'number',
        defaultValue: 20,
        placeholder: 'Default 20, Max 100',
        validation: { maxLength: 3 },
      },
    ];
  }

  return [];
}

const pushActions = [
  { label: 'Create page', value: 'create_page' },
  { label: 'Update page properties', value: 'update_page_properties' },
  { label: 'Append content to page', value: 'append_content' },
  { label: 'Create database record', value: 'create_database_record' },
  { label: 'Update database record', value: 'update_database_record' },
];

function getPushFields(action: string): FieldDefinition[] {
  if (action === 'create_page') {
    return [
      {
        name: 'parent',
        label: 'Parent',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        placeholder: 'Search pages or databases',
        validation: { required: true },
      },
      {
        name: 'title',
        label: 'Title',
        type: 'text',
        placeholder: 'Page title',
        validation: { required: true },
        hasConnectionPoint: true,
      },
      {
        name: 'content',
        label: 'Content',
        type: 'textarea',
        placeholder: 'Markdown or plain text (converted to Notion blocks)',
        hasConnectionPoint: true,
      },
    ];
  }

  if (action === 'update_page_properties') {
    return [
      {
        name: 'page_id',
        label: 'Page',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        placeholder: 'Search pages',
        validation: { required: true },
        hasConnectionPoint: true,
      },
      {
        name: 'title',
        label: 'Title',
        type: 'text',
        placeholder: 'New title (optional)',
        hasConnectionPoint: true,
      },
    ];
  }

  if (action === 'append_content') {
    return [
      {
        name: 'page_id',
        label: 'Page',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        placeholder: 'Search pages',
        validation: { required: true },
        hasConnectionPoint: true,
      },
      {
        name: 'content',
        label: 'Content',
        type: 'textarea',
        placeholder: 'Markdown or plain text (converted to Notion blocks)',
        validation: { required: true },
        hasConnectionPoint: true,
      },
    ];
  }

  if (action === 'create_database_record') {
    return [
      {
        name: 'database',
        label: 'Database',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        placeholder: 'Search databases',
        validation: { required: true },
      },
    ];
  }

  if (action === 'update_database_record') {
    return [
      {
        name: 'database',
        label: 'Database',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        placeholder: 'Search databases',
        validation: { required: true },
      },
      {
        name: 'record_id',
        label: 'Record',
        type: 'select',
        options: 'dynamic',
        remoteSearch: true,
        placeholder: 'Search records in selected database',
        validation: { required: true },
        hasConnectionPoint: true,
      },
    ];
  }

  return [];
}

export const notionConfig: IntegrationProviderConfig = {
  id: 'notion',
  name: 'Notion',
  icon: NotionIcon,
  brandColor: '#000000',
  authMethod: 'oauth2',
  ConnectionCard: NotionConnectionCard,
  pullConfig: {
    objectTypes: pullObjectTypes,
    getFields: getPullFields,
    PullPanelExtras: NotionPullPanelExtras,
  },
  pushConfig: {
    actions: pushActions,
    getFields: getPushFields,
    supportsTestPush: false,
    PushPanelExtras: NotionPushPanelExtras,
  },
  supportsPull: true,
  supportsPush: true,
  description:
    'Read and write Notion pages, databases, and blocks. Map structured records into your workflow.',
  category: ['productivity', 'content'],
  keywords: ['docs', 'wiki', 'database', 'knowledge base'],
  docsUrl: 'https://developers.notion.com/',
  priority: 95,
  supportsSharing: true,
};
